import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { 
  Bot, 
  FileText, 
  Link as LinkIcon, 
  Trash2, 
  Upload, 
  Plus,
  Settings,
  Save,
  FileQuestion,
  ToggleLeft,
  ToggleRight,
  X,
  Image as ImageIcon,
  Check
} from 'lucide-react';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const AdminChatbot = () => {
  const [activeTab, setActiveTab] = useState('settings');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Settings state
  const [settings, setSettings] = useState({
    enabled: true,
    booking_assistant_enabled: true,
    show_availability: true,
    greeting_message: '',
    fallback_message: '',
    booking_prompt: '',
    custom_instructions: ''
  });
  
  // Documents state
  const [documents, setDocuments] = useState([]);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [uploadType, setUploadType] = useState('pdf'); // 'pdf' or 'url'
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadUrl, setUploadUrl] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [uploading, setUploading] = useState(false);
  
  // FAQ state
  const [faqs, setFaqs] = useState([]);
  const [isAddFaqModalOpen, setIsAddFaqModalOpen] = useState(false);
  const [newFaq, setNewFaq] = useState({ question: '', answer: '', category: 'general' });
  
  // Media library state
  const [mediaFiles, setMediaFiles] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const [isMediaUploadModalOpen, setIsMediaUploadModalOpen] = useState(false);
  const [mediaUploadFile, setMediaUploadFile] = useState(null);
  const [mediaUploadDescription, setMediaUploadDescription] = useState('');
  const [mediaUploading, setMediaUploading] = useState(false);
  
  const { toast } = useToast();
  const { token } = useAuth();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [settingsRes, documentsRes, faqRes, mediaRes] = await Promise.all([
        axios.get(`${API}/chatbot/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chatbot/documents`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/chatbot/faq`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/media`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setSettings(settingsRes.data);
      setDocuments(documentsRes.data);
      setFaqs(faqRes.data);
      setMediaFiles(mediaRes.data);
      setSelectedAvatar(settingsRes.data.selected_avatar || '/roar-avatar.jpeg');
    } catch (error) {
      console.error('Error fetching chatbot data:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke laste chatbot-data",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      await axios.put(`${API}/chatbot/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Lagret! ✅",
        description: "Chatbot-innstillinger oppdatert"
      });
    } catch (error) {
      console.error('Error saving settings:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke lagre innstillinger",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async () => {
    try {
      setUploading(true);
      
      if (uploadType === 'pdf') {
        if (!uploadFile) {
          toast({
            title: "Feil",
            description: "Vennligst velg en PDF-fil",
            variant: "destructive"
          });
          return;
        }
        
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('description', uploadDescription);
        
        await axios.post(`${API}/chatbot/documents/pdf`, formData, {
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
      } else {
        if (!uploadUrl) {
          toast({
            title: "Feil",
            description: "Vennligst skriv inn en URL",
            variant: "destructive"
          });
          return;
        }
        
        await axios.post(`${API}/chatbot/documents/url`, null, {
          params: {
            url: uploadUrl,
            description: uploadDescription
          },
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      toast({
        title: "Suksess! ✅",
        description: "Dokument lastet opp og behandlet"
      });
      
      // Reset form and refresh
      setIsUploadModalOpen(false);
      setUploadFile(null);
      setUploadUrl('');
      setUploadDescription('');
      await fetchData();
    } catch (error) {
      console.error('Error uploading document:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke laste opp dokument",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette dokumentet?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/chatbot/documents/${docId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: "Dokument fjernet"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error deleting document:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette dokument",
        variant: "destructive"
      });
    }
  };

  const handleToggleDocument = async (docId, currentlyEnabled) => {
    try {
      await axios.put(`${API}/chatbot/documents/${docId}/toggle`, null, {
        params: { enabled: !currentlyEnabled },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Oppdatert! ✅",
        description: currentlyEnabled ? "Dokument deaktivert" : "Dokument aktivert"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error toggling document:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke oppdatere dokument",
        variant: "destructive"
      });
    }
  };

  const handleAddFaq = async () => {
    if (!newFaq.question || !newFaq.answer) {
      toast({
        title: "Feil",
        description: "Spørsmål og svar er påkrevd",
        variant: "destructive"
      });
      return;
    }
    
    try {
      await axios.post(`${API}/chatbot/faq`, null, {
        params: {
          question: newFaq.question,
          answer: newFaq.answer,
          category: newFaq.category
        },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Lagt til! ✅",
        description: "FAQ lagt til"
      });
      
      setIsAddFaqModalOpen(false);
      setNewFaq({ question: '', answer: '', category: 'general' });
      await fetchData();
    } catch (error) {
      console.error('Error adding FAQ:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke legge til FAQ",
        variant: "destructive"
      });
    }
  };

  const handleDeleteFaq = async (question) => {
    if (!window.confirm('Er du sikker på at du vil slette denne FAQ?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/chatbot/faq/${encodeURIComponent(question)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: "FAQ fjernet"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette FAQ",
        variant: "destructive"
      });
    }
  };

  const handleUploadMedia = async () => {
    if (!mediaUploadFile) {
      toast({
        title: "Feil",
        description: "Vennligst velg et bilde",
        variant: "destructive"
      });
      return;
    }

    try {
      setMediaUploading(true);
      
      const formData = new FormData();
      formData.append('file', mediaUploadFile);
      formData.append('description', mediaUploadDescription);
      
      await axios.post(`${API}/media/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast({
        title: "Lastet opp! ✅",
        description: "Bilde lagt til i mediearkivet"
      });
      
      setIsMediaUploadModalOpen(false);
      setMediaUploadFile(null);
      setMediaUploadDescription('');
      await fetchData();
    } catch (error) {
      console.error('Error uploading media:', error);
      toast({
        title: "Feil",
        description: error.response?.data?.detail || "Kunne ikke laste opp bilde",
        variant: "destructive"
      });
    } finally {
      setMediaUploading(false);
    }
  };

  const handleSelectAvatar = async (avatarUrl) => {
    try {
      await axios.put(`${API}/chatbot/avatar`, null, {
        params: { avatar_url: avatarUrl },
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSelectedAvatar(avatarUrl);
      
      toast({
        title: "Valgt! ✅",
        description: "Avatar oppdatert"
      });
    } catch (error) {
      console.error('Error selecting avatar:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke velge avatar",
        variant: "destructive"
      });
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette bildet?')) {
      return;
    }
    
    try {
      await axios.delete(`${API}/media/${mediaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast({
        title: "Slettet! ✅",
        description: "Bilde fjernet"
      });
      
      await fetchData();
    } catch (error) {
      console.error('Error deleting media:', error);
      toast({
        title: "Feil",
        description: "Kunne ikke slette bilde",
        variant: "destructive"
      });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-white text-xl">Laster chatbot-administrasjon...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold text-white mb-2">
            <span className="bg-gradient-to-r from-red-500 to-yellow-400 bg-clip-text text-transparent">
              Chatbot Administrasjon
            </span>
          </h2>
          <p className="text-gray-400">Administrer AI-chatboten, trening og innstillinger</p>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="flex space-x-2 border-b border-gray-700">
        <button
          onClick={() => setActiveTab('settings')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'settings'
              ? 'text-white border-b-2 border-red-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <Settings className="w-4 h-4 inline mr-2" />
          Innstillinger
        </button>
        <button
          onClick={() => setActiveTab('media')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'media'
              ? 'text-white border-b-2 border-red-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <ImageIcon className="w-4 h-4 inline mr-2" />
          Mediearkiv ({mediaFiles.length})
        </button>
        <button
          onClick={() => setActiveTab('documents')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'documents'
              ? 'text-white border-b-2 border-red-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileText className="w-4 h-4 inline mr-2" />
          Dokumenter ({documents.length})
        </button>
        <button
          onClick={() => setActiveTab('faq')}
          className={`px-6 py-3 font-semibold transition-colors ${
            activeTab === 'faq'
              ? 'text-white border-b-2 border-red-600'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          <FileQuestion className="w-4 h-4 inline mr-2" />
          FAQ ({faqs.length})
        </button>
      </div>

      {/* Settings Tab */}
      {activeTab === 'settings' && (
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center space-x-2">
              <Bot className="w-6 h-6 text-red-400" />
              <span>Chatbot Innstillinger</span>
            </CardTitle>
            <CardDescription className="text-gray-400">
              Konfigurer chatbotens oppførsel og meldinger
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div>
                <Label className="text-gray-300 mb-2 block">Hilsemelding</Label>
                <Textarea
                  value={settings.greeting_message}
                  onChange={(e) => setSettings({ ...settings, greeting_message: e.target.value })}
                  className="bg-gray-900/50 border-gray-700 text-white min-h-[80px]"
                  placeholder="Hei! Jeg er Sand-tec sin AI-assistent..."
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Fallback-melding</Label>
                <Textarea
                  value={settings.fallback_message}
                  onChange={(e) => setSettings({ ...settings, fallback_message: e.target.value })}
                  className="bg-gray-900/50 border-gray-700 text-white min-h-[80px]"
                  placeholder="Beklager, jeg forstod ikke helt..."
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Booking Prompt</Label>
                <Textarea
                  value={settings.booking_prompt}
                  onChange={(e) => setSettings({ ...settings, booking_prompt: e.target.value })}
                  className="bg-gray-900/50 border-gray-700 text-white min-h-[80px]"
                  placeholder="Vil du at jeg skal hjelpe deg med å booke en time?"
                />
              </div>

              <div>
                <Label className="text-gray-300 mb-2 block">Egendefinerte Instruksjoner (System Prompt)</Label>
                <Textarea
                  value={settings.custom_instructions}
                  onChange={(e) => setSettings({ ...settings, custom_instructions: e.target.value })}
                  className="bg-gray-900/50 border-gray-700 text-white min-h-[120px]"
                  placeholder="Legg til ekstra instruksjoner for hvordan chatboten skal oppføre seg..."
                />
                <p className="text-xs text-gray-500 mt-2">
                  Disse instruksjonene legges til i system prompten og påvirker chatbotens persona og oppførsel.
                </p>
              </div>

              <div className="flex items-center space-x-4 pt-4 border-t border-gray-700">
                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.enabled}
                    onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                  />
                  <span className="text-gray-300">Chatbot aktivert</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.booking_assistant_enabled}
                    onChange={(e) => setSettings({ ...settings, booking_assistant_enabled: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                  />
                  <span className="text-gray-300">Booking-assistent aktivert</span>
                </label>

                <label className="flex items-center space-x-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={settings.show_availability}
                    onChange={(e) => setSettings({ ...settings, show_availability: e.target.checked })}
                    className="w-4 h-4 rounded border-gray-700 bg-gray-900"
                  />
                  <span className="text-gray-300">Vis tilgjengelighet</span>
                </label>
              </div>
            </div>

            <Button
              onClick={handleSaveSettings}
              disabled={saving}
              className="w-full bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600 text-white font-semibold"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving ? 'Lagrer...' : 'Lagre Innstillinger'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Media Library Tab */}
      {activeTab === 'media' && (
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center space-x-2">
                  <ImageIcon className="w-6 h-6 text-purple-400" />
                  <span>Mediearkiv</span>
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Last opp bilder og velg chatbot-avatar
                </CardDescription>
              </div>
              <Button
                onClick={() => setIsMediaUploadModalOpen(true)}
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Last opp bilde
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {mediaFiles.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <ImageIcon className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Ingen bilder i mediearkivet ennå</p>
                <p className="text-sm mt-2">Last opp bilder for å velge chatbot-avatar</p>
              </div>
            ) : (
              <div>
                <div className="mb-6 p-4 bg-blue-900/20 border border-blue-700/50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <ImageIcon className="w-5 h-5 text-blue-400" />
                    <div>
                      <p className="text-white font-semibold">Nåværende Avatar</p>
                      <p className="text-sm text-gray-400">Klikk på et bilde nedenfor for å velge ny avatar</p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {mediaFiles.map((media) => (
                    <div
                      key={media.id}
                      className={`relative group cursor-pointer rounded-lg overflow-hidden border-2 transition-all ${
                        selectedAvatar === media.url
                          ? 'border-green-500 shadow-lg shadow-green-500/50'
                          : 'border-gray-700 hover:border-gray-600'
                      }`}
                      onClick={() => handleSelectAvatar(media.url)}
                    >
                      <div className="aspect-square bg-gray-900">
                        <img
                          src={`${BACKEND_URL}${media.url}`}
                          alt={media.description || media.filename}
                          className="w-full h-full object-cover"
                        />
                      </div>
                      
                      {selectedAvatar === media.url && (
                        <div className="absolute top-2 right-2 bg-green-500 rounded-full p-1">
                          <Check className="w-4 h-4 text-white" />
                        </div>
                      )}

                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteMedia(media.id);
                          }}
                          variant="destructive"
                          size="sm"
                          className="bg-red-600 hover:bg-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Slett
                        </Button>
                      </div>

                      {media.description && (
                        <div className="absolute bottom-0 left-0 right-0 bg-black/80 p-2">
                          <p className="text-xs text-white truncate">{media.description}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-4">
          <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-white flex items-center space-x-2">
                    <FileText className="w-6 h-6 text-blue-400" />
                    <span>Treningsdokumenter</span>
                  </CardTitle>
                  <CardDescription className="text-gray-400">
                    Last opp PDFs eller legg til nettside-URLer for å trene chatboten
                  </CardDescription>
                </div>
                <Button
                  onClick={() => setIsUploadModalOpen(true)}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Last opp
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                  <p className="text-lg">Ingen dokumenter lastet opp ennå</p>
                  <p className="text-sm mt-2">Last opp PDF-filer eller legg til nettside-URLer for å trene chatboten</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {documents.map((doc, index) => (
                    <div
                      key={index}
                      className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 flex items-center justify-between hover:border-gray-600 transition-colors"
                    >
                      <div className="flex items-center space-x-4 flex-1">
                        {doc.type === 'pdf' ? (
                          <FileText className="w-8 h-8 text-red-400" />
                        ) : (
                          <LinkIcon className="w-8 h-8 text-blue-400" />
                        )}
                        <div className="flex-1">
                          <h4 className="text-white font-semibold">
                            {doc.type === 'pdf' ? doc.filename : doc.title}
                          </h4>
                          {doc.description && (
                            <p className="text-sm text-gray-400">{doc.description}</p>
                          )}
                          {doc.type === 'url' && (
                            <p className="text-xs text-gray-500 mt-1">{doc.url}</p>
                          )}
                          <div className="flex items-center space-x-3 mt-2">
                            {doc.type === 'pdf' && (
                              <span className="text-xs text-gray-500">{doc.page_count} sider</span>
                            )}
                            <span className="text-xs text-gray-500">
                              {doc.content_length ? `${(doc.content_length / 1000).toFixed(1)}k tegn` : ''}
                            </span>
                            <Badge variant={doc.enabled ? "default" : "secondary"} className="text-xs">
                              {doc.enabled ? 'Aktivert' : 'Deaktivert'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button
                          onClick={() => handleToggleDocument(doc.id || doc._id, doc.enabled)}
                          variant="outline"
                          size="sm"
                          className="border-gray-600 text-gray-300 hover:bg-gray-700"
                        >
                          {doc.enabled ? (
                            <ToggleRight className="w-4 h-4" />
                          ) : (
                            <ToggleLeft className="w-4 h-4" />
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDeleteDocument(doc.id || doc._id)}
                          variant="outline"
                          size="sm"
                          className="border-red-600 text-red-400 hover:bg-red-600/20"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* FAQ Tab */}
      {activeTab === 'faq' && (
        <Card className="bg-gradient-to-br from-gray-800/80 to-gray-900/80 border-gray-700">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-white flex items-center space-x-2">
                  <FileQuestion className="w-6 h-6 text-green-400" />
                  <span>FAQ Administrasjon</span>
                </CardTitle>
                <CardDescription className="text-gray-400">
                  Administrer ofte stilte spørsmål og svar
                </CardDescription>
              </div>
              <Button
                onClick={() => setIsAddFaqModalOpen(true)}
                className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Legg til FAQ
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {faqs.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <FileQuestion className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg">Ingen FAQ-elementer ennå</p>
              </div>
            ) : (
              <div className="space-y-3">
                {faqs.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-gray-900/50 border border-gray-700 rounded-lg p-4 hover:border-gray-600 transition-colors"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h4 className="text-white font-semibold">{faq.question}</h4>
                          <Badge variant="outline" className="text-xs">
                            {faq.category}
                          </Badge>
                        </div>
                        <p className="text-gray-400 text-sm">{faq.answer}</p>
                      </div>
                      <Button
                        onClick={() => handleDeleteFaq(faq.question)}
                        variant="outline"
                        size="sm"
                        className="border-red-600 text-red-400 hover:bg-red-600/20 ml-4"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upload Document Modal */}
      <Dialog open={isUploadModalOpen} onOpenChange={setIsUploadModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-2xl">
          <DialogHeader>
            <DialogTitle>Last opp dokument</DialogTitle>
            <DialogDescription className="text-gray-400">
              Last opp en PDF-fil eller legg til en nettside-URL
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Type selector */}
            <div className="flex space-x-2">
              <Button
                onClick={() => setUploadType('pdf')}
                variant={uploadType === 'pdf' ? 'default' : 'outline'}
                className={uploadType === 'pdf' ? 'bg-red-600' : 'border-gray-600'}
              >
                <FileText className="w-4 h-4 mr-2" />
                PDF-fil
              </Button>
              <Button
                onClick={() => setUploadType('url')}
                variant={uploadType === 'url' ? 'default' : 'outline'}
                className={uploadType === 'url' ? 'bg-red-600' : 'border-gray-600'}
              >
                <LinkIcon className="w-4 h-4 mr-2" />
                Nettside-URL
              </Button>
            </div>

            {/* PDF Upload */}
            {uploadType === 'pdf' && (
              <div>
                <Label className="text-gray-300 mb-2 block">Velg PDF-fil</Label>
                <Input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setUploadFile(e.target.files[0])}
                  className="bg-gray-800 border-gray-700 text-white"
                />
                {uploadFile && (
                  <p className="text-sm text-gray-400 mt-2">Valgt: {uploadFile.name}</p>
                )}
              </div>
            )}

            {/* URL Input */}
            {uploadType === 'url' && (
              <div>
                <Label className="text-gray-300 mb-2 block">Nettside-URL</Label>
                <Input
                  type="url"
                  value={uploadUrl}
                  onChange={(e) => setUploadUrl(e.target.value)}
                  placeholder="https://example.com/side"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>
            )}

            {/* Description */}
            <div>
              <Label className="text-gray-300 mb-2 block">Beskrivelse (valgfritt)</Label>
              <Textarea
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                placeholder="Kort beskrivelse av dokumentet..."
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsUploadModalOpen(false);
                setUploadFile(null);
                setUploadUrl('');
                setUploadDescription('');
              }}
              className="border-gray-600 text-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleUploadDocument}
              disabled={uploading}
              className="bg-gradient-to-r from-red-600 to-yellow-500 hover:from-red-700 hover:to-yellow-600"
            >
              <Upload className="w-4 h-4 mr-2" />
              {uploading ? 'Laster opp...' : 'Last opp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Upload Media Modal */}
      <Dialog open={isMediaUploadModalOpen} onOpenChange={setIsMediaUploadModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Last opp bilde</DialogTitle>
            <DialogDescription className="text-gray-400">
              Last opp et bilde til mediearkivet
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Velg bilde</Label>
              <Input
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                onChange={(e) => setMediaUploadFile(e.target.files[0])}
                className="bg-gray-800 border-gray-700 text-white"
              />
              {mediaUploadFile && (
                <p className="text-sm text-gray-400 mt-2">Valgt: {mediaUploadFile.name}</p>
              )}
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Beskrivelse (valgfritt)</Label>
              <Textarea
                value={mediaUploadDescription}
                onChange={(e) => setMediaUploadDescription(e.target.value)}
                placeholder="Kort beskrivelse av bildet..."
                className="bg-gray-800 border-gray-700 text-white min-h-[80px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsMediaUploadModalOpen(false);
                setMediaUploadFile(null);
                setMediaUploadDescription('');
              }}
              className="border-gray-600 text-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleUploadMedia}
              disabled={mediaUploading}
              className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
            >
              <Upload className="w-4 h-4 mr-2" />
              {mediaUploading ? 'Laster opp...' : 'Last opp'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add FAQ Modal */}
      <Dialog open={isAddFaqModalOpen} onOpenChange={setIsAddFaqModalOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Legg til FAQ</DialogTitle>
            <DialogDescription className="text-gray-400">
              Legg til et nytt ofte stilt spørsmål
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Spørsmål</Label>
              <Input
                value={newFaq.question}
                onChange={(e) => setNewFaq({ ...newFaq, question: e.target.value })}
                placeholder="Skriv inn spørsmålet..."
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Svar</Label>
              <Textarea
                value={newFaq.answer}
                onChange={(e) => setNewFaq({ ...newFaq, answer: e.target.value })}
                placeholder="Skriv inn svaret..."
                className="bg-gray-800 border-gray-700 text-white min-h-[120px]"
              />
            </div>

            <div>
              <Label className="text-gray-300 mb-2 block">Kategori</Label>
              <Input
                value={newFaq.category}
                onChange={(e) => setNewFaq({ ...newFaq, category: e.target.value })}
                placeholder="general, eu-kontroll, service, etc."
                className="bg-gray-800 border-gray-700 text-white"
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setIsAddFaqModalOpen(false);
                setNewFaq({ question: '', answer: '', category: 'general' });
              }}
              className="border-gray-600 text-gray-300"
            >
              <X className="w-4 h-4 mr-2" />
              Avbryt
            </Button>
            <Button
              onClick={handleAddFaq}
              className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              Legg til
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminChatbot;
